import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { loadBillingHistory, loadBillingSummary, type BillingSummary } from '@/features/billing'
import { useAuth } from '@/features/auth'
import { useUserStore } from '@/entities/user'
import { VideosShell } from '@/widgets/videos-shell'
import { routes } from '@/shared/config'
import { designTokens } from '@/shared/lib/design-tokens'
import { cn } from '@/shared/lib'
import {
  DashboardSubscriptionDesktop,
  type DashboardSubscriptionShellVariant,
  type DashboardSubscriptionUiStatus,
} from './dashboard-subscription-page-desktop'
import { DashboardSubscriptionMobile } from './dashboard-subscription-page-mobile'

const amountFormatter = new Intl.NumberFormat('ru-RU')
const shortDateFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
})
export const BILLING_SUMMARY_STALE_TIME_MS = 30_000
export const BILLING_SUMMARY_REFETCH_INTERVAL_MS = 30_000
export const BILLING_HISTORY_STALE_TIME_MS = 60_000
export const BILLING_HISTORY_REFETCH_INTERVAL_MS = 60_000

const statusToViewStatus = (status: string | null | undefined): DashboardSubscriptionUiStatus => {
  switch (status) {
    case 'active':
    case 'in_trial':
      return 'active'
    case 'pending_cancellation':
      return 'pending_cancellation'
    case 'on_hold':
      return 'on_hold'
    default:
      return 'not_active'
  }
}

const isSubscriberLikeStatus = (status: string | null | undefined) =>
  status === 'active' ||
  status === 'in_trial' ||
  status === 'pending_cancellation' ||
  status === 'on_hold'

const formatDate = (value: Date | null) => (value ? shortDateFormatter.format(value) : null)

const formatAmount = (value: number | null) =>
  value != null ? `${amountFormatter.format(value)} ₽` : null

const addMonths = (value: Date, months: number) => {
  const result = new Date(value)
  result.setMonth(result.getMonth() + months)
  return result
}

const buildPlanTitle = (summary: BillingSummary | null, status: DashboardSubscriptionUiStatus) => {
  if (status === 'not_active') {
    return 'У вас пока нет активной подписки'
  }

  const resolvedPlanName =
    summary?.currentPrice?.name ?? summary?.nextPrice?.name ?? 'Подписка Epic Growth'

  return `Ваш тариф — ${resolvedPlanName}`
}

// ЛК read-only (task-lk-readonly-lite): автосписаний нет (CloudPayments мёртв с
// 14.08.2026). Поэтому — никаких «будет продлена за N ₽»: только честный срок
// действия и путь в Академию.
const buildRenewalText = (
  summary: BillingSummary | null,
  status: DashboardSubscriptionUiStatus
) => {
  if (status === 'not_active') {
    return null
  }

  const validUntil = formatDate(summary?.nextTransactionAt ?? null)

  if (status === 'pending_cancellation') {
    return validUntil
      ? `Автопродление отключено. Доступ сохраняется до ${validUntil}, дальше — Академия.`
      : 'Автопродление отключено. Дальше — Академия.'
  }

  if (status === 'on_hold') {
    return 'Оплата недоступна. Доступ к материалам — в Академии.'
  }

  if (validUntil) {
    return `Подписка активна до ${validUntil}`
  }

  return summary?.currentPrice?.name ? `Текущий тариф: ${summary.currentPrice.name}` : null
}

const buildPromoText = (summary: BillingSummary | null, status: DashboardSubscriptionUiStatus) => {
  if (status === 'not_active' || !summary?.promo?.promoCode) {
    return null
  }

  const displayedPercent = summary.promo.discountDisplayedPercent
  if (displayedPercent != null) {
    return `Промокод ${summary.promo.promoCode} активен, скидка ${displayedPercent}%`
  }

  return `Промокод ${summary.promo.promoCode} активен`
}

const buildPromoBadgeText = (
  summary: BillingSummary | null,
  status: DashboardSubscriptionUiStatus
) => {
  if ((status !== 'active' && status !== 'pending_cancellation') || !summary?.promo?.promoCode) {
    return null
  }

  const displayedPercent = summary.promo.discountDisplayedPercent
  if (displayedPercent != null) {
    const promoPeriodsUsed = summary.promo.promoPeriodsUsed ?? 0
    const promoPeriodsTotal = summary.promo.promoPeriodsTotal ?? null
    const remainingPromoPeriods =
      promoPeriodsTotal != null ? Math.max(promoPeriodsTotal - promoPeriodsUsed, 0) : null
    const renewalPeriodMonths = summary.nextPrice?.period ?? summary.currentPrice?.period ?? null

    if (
      remainingPromoPeriods != null &&
      remainingPromoPeriods > 0 &&
      renewalPeriodMonths != null &&
      summary.nextTransactionAt
    ) {
      const promoValidUntil = addMonths(
        summary.nextTransactionAt,
        Math.max(remainingPromoPeriods - 1, 0) * renewalPeriodMonths
      )
      const formattedPromoValidUntil = formatDate(promoValidUntil)
      if (formattedPromoValidUntil) {
        return `-${displayedPercent}% до ${formattedPromoValidUntil}`
      }
    }

    return `-${displayedPercent}%`
  }

  return summary.promo.promoCode
}

function SubscriptionManagementPageContent({
  shellVariant,
}: {
  shellVariant: DashboardSubscriptionShellVariant
}) {
  const user = useUserStore((state) => state.user)
  const billingSummaryQuery = useQuery({
    queryKey: ['billing', 'summary'],
    queryFn: loadBillingSummary,
    staleTime: BILLING_SUMMARY_STALE_TIME_MS,
    refetchInterval: BILLING_SUMMARY_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
  const billingHistoryQuery = useQuery({
    queryKey: ['billing', 'history'],
    queryFn: loadBillingHistory,
    staleTime: BILLING_HISTORY_STALE_TIME_MS,
    refetchInterval: BILLING_HISTORY_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  const shouldDelayInactiveFallback =
    !billingSummaryQuery.data &&
    (billingSummaryQuery.status === 'pending' ||
      (billingSummaryQuery.fetchStatus === 'fetching' &&
        isSubscriberLikeStatus(user?.subscriptionStatus)))

  if (shouldDelayInactiveFallback) {
    return null
  }

  const summary = billingSummaryQuery.data ?? null
  const summaryStatus = summary?.status
  const userStatus = user?.subscriptionStatus
  const fallbackStatusWhileRefreshing =
    billingSummaryQuery.fetchStatus === 'fetching' &&
    isSubscriberLikeStatus(userStatus) &&
    !isSubscriberLikeStatus(summaryStatus)
      ? userStatus
      : null
  const resolvedStatus = statusToViewStatus(
    fallbackStatusWhileRefreshing ?? summaryStatus ?? userStatus
  )
  const isPromoBadgeVisible =
    resolvedStatus === 'active' || resolvedStatus === 'pending_cancellation'
  const planTitle = buildPlanTitle(summary, resolvedStatus)
  const renewalText = buildRenewalText(summary, resolvedStatus)
  const promoText = buildPromoText(summary, isPromoBadgeVisible ? resolvedStatus : 'not_active')
  const promoBadgeText = buildPromoBadgeText(
    summary,
    isPromoBadgeVisible ? resolvedStatus : 'not_active'
  )
  const paymentHistoryItems = (billingHistoryQuery.data ?? []).map((item) => ({
    status: item.declined ? ('declined' as const) : ('success' as const),
    date: formatDate(item.paidAt) ?? '—',
    planName: item.description ?? item.operationId ?? 'Платеж Epic Growth',
    amount: formatAmount(item.amountCents) ?? '—',
  }))

  return (
    <>
      <DashboardSubscriptionDesktop
        shellVariant={shellVariant}
        status={resolvedStatus}
        planTitle={planTitle}
        renewalText={renewalText}
        promoBadgeText={promoBadgeText}
        paymentHistoryItems={paymentHistoryItems}
      />
      <DashboardSubscriptionMobile
        shellVariant={shellVariant}
        status={resolvedStatus}
        planTitle={planTitle}
        renewalText={renewalText}
        promoText={promoText}
        paymentHistoryItems={paymentHistoryItems}
      />
    </>
  )
}

export function DashboardSubscriptionPage() {
  return (
    <section className="flex w-full flex-col">
      <SubscriptionManagementPageContent shellVariant="dashboard" />
    </section>
  )
}

export function CatalogSubscriptionPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { logout } = useAuth()

  return (
    <div className="min-h-screen" style={{ backgroundColor: designTokens.colors.neutral.black }}>
      <VideosShell
        activeNavigationItem="subscription"
        currentPathname={location.pathname}
        fullHeight={false}
        showSearchControl={false}
        showFilterControls={false}
        onLogout={() => {
          void logout().then(() => {
            navigate(routes.registration, { replace: true })
          })
        }}
      >
        <main
          className={cn(
            'flex min-h-0 w-full flex-1 overflow-x-hidden overflow-y-auto px-4 pb-8 pt-6 lg:px-6 lg:pb-10'
          )}
        >
          <div className="mx-auto flex w-full max-w-[1306px] min-w-0 flex-col">
            <SubscriptionManagementPageContent shellVariant="catalog" />
          </div>
        </main>
      </VideosShell>
    </div>
  )
}
